import classnames from 'classnames'
import { FC, useEffect, useRef } from 'react'

import useGallery, { Image, UseGalleryParams } from 'hooks/useGallery'
import Icon from '~ui/Icon'

import styles from './SlideshowGallery.module.scss'

type Size = {
  width: number
  height: number
}

type Thumbnail = {
  size?: Size
  images: Image[]
  hide?: boolean
}

export type SlideshowGalleryProps = {
  thumbnail: Thumbnail
} & UseGalleryParams

const SlideshowGallery: FC<SlideshowGalleryProps> = ({
  images,
  interval,
  thumbnail,
  swipeCoefficient = 0.5,
}) => {
  const { index, setIndex, onNext, onPrev, smooth, galleryRef, slidesRef } = useGallery({
    images,
    interval,
    swipeCoefficient,
  })

  const thumbnailsRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!thumbnailsRef.current) return

    const width = thumbnailsRef.current.offsetWidth

    thumbnailsRef.current.scrollTo({
      behavior: 'smooth',
      left: thumbnail.size.width * index - width / 2 + thumbnail.size.width / 2,
    })
  }, [images.length, index, thumbnail.size.width])

  return (
    <div className={styles.wrapper}>
      <div className={styles.gallery} ref={galleryRef}>
        <div
          ref={slidesRef}
          className={classnames(styles.slides, { [styles['slides-smooth']]: smooth })}
        >
          {images.map(({ url, description, key }, i) => (
            <div key={key || url || i} className={styles.slide}>
              <img src={url} alt={description || `slide-${i + 1}`} />
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={onPrev}
          className={classnames(styles.button, styles['button-prev'])}
        >
          <Icon name="left-arrow" fill="currentColor" width="1em" height="1em" />
        </button>
        <button
          type="button"
          onClick={onNext}
          className={classnames(styles.button, styles['button-next'])}
        >
          <Icon name="right-arrow" fill="currentColor" width="1em" height="1em" />
        </button>
      </div>

      {!thumbnail.hide && (
        <div className={styles['thumbnails-wrapper']}>
          <div className={styles.thumbnails} ref={thumbnailsRef}>
            {thumbnail.images.map(({ url }, i) => (
              <div
                key={url || i}
                onClick={() => setIndex(i)}
                className={classnames(styles.thumbnail, {
                  [styles['thumbnail-active']]: i === index,
                })}
                aria-hidden
                style={{
                  flex: `0 0 ${thumbnail.size.width}px`,
                }}
              >
                <img
                  src={url}
                  alt={`thumbnail-${i + 1}`}
                  height={thumbnail.size.height}
                  width={thumbnail.size.width}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default SlideshowGallery
